import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { Buffer } from '@bynder/design-system';
import { PageWithAppcues } from 'packages/helpers/RouteWithAppcues';
import { ListHeader } from './ListHeader';
import { ListRow, ListRowHandle } from './ListRow';
import { Container } from './Container';
import useExport from '../hooks/useExport';
import { LoaderTrigger } from './List.styled';

export const List = () => {
    const [rowRefs, setRowRefs] = useState<React.RefObject<ListRowHandle>[]>([]);
    const { items, loadingNextPart, loadNextPart, total } = useExport();
    const loaderTriggerRef = useRef(null);
    const length = items.length;

    const handleScroll = useCallback(() => {
        if (loadingNextPart) {
            return;
        }

        loadNextPart();
    }, [loadingNextPart, loadNextPart]);

    useEffect(() => {
        if (!loaderTriggerRef.current || loadingNextPart || length >= total) {
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    handleScroll();
                }
            });
        });

        observer.observe(loaderTriggerRef.current);

        return () => {
            observer.disconnect();
        };
    }, [handleScroll, loadingNextPart, total]);

    useEffect(() => {
        setRowRefs((rowRefs) =>
            Array(length)
                .fill(undefined)
                .map((_, i) => rowRefs[i] || createRef<ListRowHandle>()),
        );
    }, [length]);

    return (
        <PageWithAppcues>
            <ListHeader rowRefs={rowRefs} />
            <div id="export_modal_list">
                <Container>
                    {items.map((item, index) => (
                        <ListRow
                            ref={rowRefs[index]}
                            key={item.variationSetId ?? item.id ?? item.creativeVersionPageId ?? item.pageId}
                            item={item}
                            isLast={index === items.length - 1}
                            isSingle={items.length === 1}
                        />
                    ))}
                    {loadingNextPart && <Buffer />}
                </Container>
            </div>
            <LoaderTrigger ref={loaderTriggerRef} />
        </PageWithAppcues>
    );
};
